import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Category, Prompt } from '../types';
import {
  isValidMarker,
  normalizeMarker,
  savePrompt,
} from '../utils/firebaseService';
import {
  ref,
  uploadBytesResumable,
  deleteObject,
  getDownloadURL
} from 'firebase/storage';
import { storage } from '../config/firebase';
import imageCompression
  from 'browser-image-compression';

const DEFAULT_OBSERVATION =
  '(OPCIONAL) Antes de utilizar este prompt, revise as a aba de caracteristicas físicas da modelo descritas (como tipo de rosto, cabelo, cor da pele e traços faciais). Caso não correspondam ao perfil desejado, adapte essas informações para alinhar com a modelo que será utilizada. As demais características, como expressão e estilo, já são definidas diretamente no prompt e não precisam ser alteradas.';

interface PromptFormProps {
  onSubmit: (prompt: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  initialData?: Prompt;
  categories: Category[];
  isLoading?: boolean;
  onCancel?: () => void;
  userId?: string; // ID do usuário atual
}

const PromptForm: React.FC<PromptFormProps> = ({
  onSubmit,
  initialData,
  categories,
  isLoading = false,
  onCancel,
  userId,
}) => {

  const [formData, setFormData] = useState({
    content: initialData?.content || '',
    categoryId:initialData?.categoryId || '',
    categoryName:initialData?.categoryName || '',
    author: initialData?.author || '',
    identifier: initialData?.identifier || '',
    observation: initialData ? initialData.observation ?? '' : DEFAULT_OBSERVATION,
    imageUrl: initialData?.imageUrl || '',
    imagePath: initialData?.imagePath || '', 
isPublic:
  initialData?.isPublic ?? false,
    isSensitive:
  initialData?.isSensitive
  ?? false,

  });

  const [uploadingImage, setUploadingImage] =
  useState(false);
  const [uploadProgress, setUploadProgress] =
  useState(0);

  const originalImagePathRef = useRef(initialData?.imagePath || '');
  const stagedImagePathRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  const deleteUnpersistedImage = async (imagePath: string) => {
    try {
      await deleteObject(ref(storage, imagePath));
    } catch (cleanupError) {
      console.warn('Erro ao limpar imagem ainda não vinculada:', cleanupError);
    }
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      const stagedImagePath = stagedImagePathRef.current;
      if (stagedImagePath) {
        void deleteUnpersistedImage(stagedImagePath);
      }
    };
  }, []);

  const filteredCategories = categories.filter((cat) => {

  // modo GLOBAL
  if (formData.isPublic) {

    return cat.type === 'global';
  }

  // modo PESSOAL
  return (
    cat.type === 'personal'
    &&
    cat.ownerId === userId
  );
  
});


  const [variations, setVariations] = useState<string[]>(
    initialData?.variations || []
  );

  const [markers, setMarkers] = useState<string[]>(
  initialData?.markers || []
);

  const [markerInput, setMarkerInput] = useState('');

  const [error, setError] = useState<string>('');

  const addVariation = () => {
    setVariations([...variations, '']);
  };

  const updateVariation = (index: number, value: string) => {
    const newVariations = [...variations];
    newVariations[index] = value;
    setVariations(newVariations);
  };

  const removeVariation = (index: number) => {
    setVariations(variations.filter((_, i) => i !== index));
  };

 const addMarker = () => {

  const normalizedMarker = normalizeMarker(markerInput);

if (!isValidMarker(normalizedMarker)) {

  setError('Marcador invalido. Use letras minusculas e ate tres numeros ao final.');

  return;
}

  if (!normalizedMarker) {
    return;
  }

  if (normalizedMarker.length < 2) {
    setError(
      'Marcadores devem ter pelo menos 2 caracteres.'
    );
    return;
  }

  if (normalizedMarker.length > 10) {
    setError(
      'Marcadores devem ter no máximo 10 caracteres.'
    );
    return;
  }


  if (markers.some((marker) => normalizeMarker(marker) === normalizedMarker)) {
    setError(
      'Marcador já adicionado.'
    );
    return;
  }

  if (markers.length >= 3) {
    setError(
      'Máximo de 3 marcadores.'
    );
    return;
  }

  setMarkers([
    ...markers,
    normalizedMarker
  ]);

  setMarkerInput('');
  setError('');
};

const removeMarker = (
  marker: string
) => {

  setMarkers(
    markers.filter(
      m => m !== marker
    )
  );
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    console.log('SUBMIT', {
  isPublic: formData.isPublic,
  categoryId: formData.categoryId,
  categoryName: formData.categoryName,
});

    // Prepara dados para salvamento
    const promptData = {
      ...formData,
      variations: variations.length > 0 ? variations : undefined,

      markers,
    };

    // Verifica se userId está disponível
    if (!userId) {
      setError('Usuário não identificado. Faça login novamente.');
      return;
    }
    // Usa função segura de salvamento, passando ID se for edição

if (

  formData.isPublic

  &&

  !formData.categoryId

) {

  setError(
    'Selecione uma categoria.'
  );

  return;
}

if (

  !formData.isPublic

  &&

  !formData.categoryName.trim()

) {

  setError(
    'Informe uma categoria.'
  );

  return;
}
    
    const result = await savePrompt(

  promptData,

  userId,

  formData.isPublic,

  initialData?.id

);

    if (!result.success) {
      setError(result.error || 'Erro ao salvar prompt');
      return;
    }

    const previousImagePath = originalImagePathRef.current;
    const savedImagePath = formData.imagePath;

    stagedImagePathRef.current = null;
    originalImagePathRef.current = savedImagePath;

    if (
      previousImagePath
      && previousImagePath !== savedImagePath
    ) {
      void deleteUnpersistedImage(previousImagePath);
    }

    // Sucesso - chama callback do componente pai se existir
    if (onSubmit) {
      await onSubmit(result.prompt!);
    }

    // Reset do formulário em caso de sucesso
    setFormData({
      content: '',
      categoryId: '',
      categoryName: '',
      author: '',
      isPublic: false,
      identifier: '',
      observation:
      DEFAULT_OBSERVATION,
      imageUrl: '',
      isSensitive: false,
      imagePath: '',
    });
    setVariations([]);
    setMarkers([]);
    setMarkerInput('');
  };

  // Função para upload de imagem
  const handleImageUpload = async (
  file: File
) => {

  try {

    setUploadingImage(true);

    // =====================================
    // valida tamanho
    // =====================================

    if (file.size > 5 * 1024 * 1024) {

      // =====================================
// compressão inteligente
// =====================================

      setError('Imagem muito grande. Máximo 5MB.');
      

      return;
    }

    const compressedFile =
  await imageCompression(file, {

    maxSizeMB: 0.7,

    maxWidthOrHeight: 1920,

    useWebWorker: true,

    fileType: 'image/webp',

    initialQuality: 0.82,
});

    // =====================================
    // path único
    // =====================================

    const fileId =
      typeof crypto !== 'undefined'
      && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const fileName =
      `${fileId}.webp`;

    const storageRef = ref(

      storage,

      `prompts/${userId}/${fileName}`
    );

    const storagePath =
  storageRef.fullPath;

    // =====================================
    // upload
    // =====================================
const uploadTask =
  uploadBytesResumable(

    storageRef,

    compressedFile
  );

  await new Promise<void>(

  (resolve, reject) => {

    uploadTask.on(

      'state_changed',

      (snapshot) => {

        const progress =

          (snapshot.bytesTransferred /
            snapshot.totalBytes) * 100;

        setUploadProgress(
          Math.round(progress)
        );
      },

      (error) => {

        reject(error);
      },

      () => {

        resolve();
      }
    );
  }
);

    // =====================================
    // url pública
    // =====================================

    const downloadURL =
      await getDownloadURL(storageRef);

    if (!isMountedRef.current) {
      await deleteUnpersistedImage(storagePath);
      return;
    }

    const previousStagedImagePath =
      stagedImagePathRef.current;

    if (
      previousStagedImagePath
      && previousStagedImagePath !== storagePath
    ) {
      await deleteUnpersistedImage(previousStagedImagePath);
    }

    stagedImagePathRef.current = storagePath;

    // =====================================
    // salva no form
    // =====================================

    setFormData({

  ...formData,

  imageUrl: downloadURL,

  imagePath: storagePath
});
  } catch (error) {

    console.error(error);

    setError('Erro ao enviar imagem. Tente novamente.');

  } finally {

    setUploadingImage(false);
    setUploadProgress(0);
  }
};

const handleRemoveImage = async () => {

  try {

    if (!formData.imageUrl)
      return;

console.log(
  'REMOVE IMAGE',
  {
    imageUrl: formData.imageUrl,
    imagePath: formData.imagePath
  }
);

    const stagedImagePath = stagedImagePathRef.current;

    if (stagedImagePath) {
      await deleteUnpersistedImage(stagedImagePath);
      stagedImagePathRef.current = null;
    }

    setFormData({

   ...formData,

   imageUrl: '',

   imagePath: ''

});

  } catch (error) {

    console.error(error);

    setError('Erro ao remover imagem. Tente novamente.');
  }
};

  return (
    <form onSubmit={handleSubmit} className="bg-dark-800 rounded-lg border border-dark-700 p-6 space-y-4">
      {error && (
        <div className="p-3 bg-red-900/20 border border-red-700 rounded text-red-200">
          {error}
        </div>
      )}

      <div>
        <label className="block text-dark-200 font-medium mb-2">Categoria *</label>
       {formData.isPublic ? (

  <select
    value={formData.categoryId}
    onChange={(e) => {

  const selectedCategory =
    filteredCategories.find(
      cat => cat.id === e.target.value
    );

  setFormData({
    ...formData,

    categoryId: e.target.value,

    categoryName:
      selectedCategory?.name || ''
  });
}}
    className="w-full px-3 py-2 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 focus:outline-none focus:border-purple-500"
  >

    <option value="">
      Selecione uma categoria
    </option>

    {filteredCategories.map((cat) => (

      <option
        key={cat.id}
        value={cat.id}
      >
        {cat.name}
      </option>

    ))}

  </select>

) : (

  <input
    type="text"
    value={formData.categoryName}
    onChange={(e) =>
      setFormData({
        ...formData,
        categoryName: e.target.value
      })
    }
    className="w-full px-3 py-2 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 focus:outline-none focus:border-purple-500"
    placeholder="Digite ou crie uma categoria"
  />

)}
      </div>

      <div>
        <label className="block text-dark-200 font-medium mb-2">Identificador (opcional)</label>
        <input
          type="text"
          value={formData.identifier}
          onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
          className="w-full px-3 py-2 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 focus:outline-none focus:border-purple-500"
          placeholder="Ex: v1.0, premium, etc."
        />
      </div>

      {/* Controle de Visibilidade */}
      <div>
        <label className="block text-dark-200 font-medium mb-3">Visibilidade</label>
        <div className="space-y-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="visibility"
              checked={!formData.isPublic}
              onChange={() =>
              setFormData({
              ...formData,
              isPublic: false,
              categoryId: '',
              categoryName: ''
            })
                }
              className="h-4 w-4 text-purple-600 bg-dark-900 border-dark-700 focus:ring-purple-500"
            />
            <span className="text-dark-100">📚 Biblioteca pessoal</span>
          </label>

          
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="visibility"
              checked={formData.isPublic}
              onChange={() =>
              setFormData({
              ...formData,
              isPublic: true,
              categoryId: '',
              categoryName: ''
             })
                  }
              className="h-4 w-4 text-purple-600 bg-dark-900 border-dark-700 focus:ring-purple-500"
            />
            <span className="text-dark-100">🌍 Publicar globalmente</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-dark-200 font-medium mb-2">Conteúdo *</label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          rows={8}
          className="w-full px-3 py-2 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 focus:outline-none focus:border-purple-500 resize-none"
          placeholder="Conteúdo do prompt"
        />
      </div>

      {/* Sistema de Variações */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-dark-200 font-medium">
            Variações ({variations.length})
          </label>
          <button
            type="button"
            onClick={addVariation}
            className="text-purple-400 hover:text-purple-300 text-sm font-medium"
          >
            + Adicionar variação
          </button>
        </div>

        {variations.length > 0 && (
          <div className="space-y-3">
            {variations.map((variation, index) => (
              <div key={index} className="relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-dark-300 text-sm">Var {index + 1}</label>
                  <button
                    type="button"
                    onClick={() => removeVariation(index)}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Remover
                  </button>
                </div>
                <textarea
                  value={variation}
                  onChange={(e) => updateVariation(index, e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 focus:outline-none focus:border-purple-500 resize-none"
                  placeholder={`Variação ${index + 1} do prompt`}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input
          id="isSensitive"
          type="checkbox"
          checked={formData.isSensitive}
          onChange={(e) =>
  setFormData({
    ...formData,

    // novo domínio real
    isSensitive: e.target.checked,

  })
}
          className="h-4 w-4 text-purple-600 bg-dark-900 border-dark-700 rounded focus:ring-purple-500"
        />
        <label htmlFor="isSensitive" className="text-dark-100 font-medium">
          Conteúdo sensível
        </label>
      </div>

   {/* Marcadores */}

<div className="space-y-2">

  <div className="flex items-center justify-between">

  <label className="font-medium text-white">
    Marcadores
  </label>

  <span className="text-xs text-slate-400">
    {markers.length}/3
  </span>

</div>

  <div
  className="
    flex
    items-center
    gap-3
    bg-dark-900
    border
    border-dark-700
    rounded-lg
    px-4
    py-2
  "
>

    <input
  type="text"
  value={markerInput}
  onChange={(e) =>
    setMarkerInput(e.target.value)
  }
  placeholder="Ex: loira"
  maxLength={10}
  className="
    flex-1
    bg-transparent
    text-white
    placeholder:text-slate-500
    outline-none
    text-sm
    block text-dark-200 font-medium
  "
/>

    <button
      type="button"
      onClick={addMarker}
      disabled={markers.length >= 3}
      className="
  px-3
  py-1.5
  text-sm
  rounded-lg
  bg-purple-600
  hover:bg-purple-500
  text-white
  transition-colors
  disabled:opacity-50
  disabled:cursor-not-allowed
"
    >
      Adicionar
    </button>

  </div>

  <p className="text-xs text-slate-400 mt-2">

  Até 3 marcadores • 2–10 caracteres
</p>

  <div className="flex flex-wrap gap-2">

    {markers.map((marker) => (

      <span
  key={marker}
  className="
  flex
  items-center
  gap-1
  px-2
  py-0.4
  rounded-full
  bg-purple-500/10
  border
  border-purple-500/15
  text-purple-300
  font-medium 
"
>

        #{marker}

        <button
  type="button"
  onClick={() =>
    removeMarker(marker)
  }
  className="
    text-red-300
    hover:text-red-300
    transition-colors
  "
>
  ×
</button>

      </span>

    ))}

  </div>

</div>

      <div>
        <label className="block text-dark-200 font-medium mb-2">Observação</label>
        <textarea
          value={formData.observation}
          onChange={(e) => setFormData({ ...formData, observation: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 focus:outline-none focus:border-purple-500 resize-none"
          placeholder="Observações sobre o prompt (opcional)"
        />
      </div>

      <div>
        <label className="block text-dark-200 font-medium mb-2">URL da Imagem</label>
        <div className="space-y-3">
          {!formData.imageUrl && (
          <label

  onDragOver={(e) => {

    e.preventDefault();

  }}

  onDragLeave={() => {
  }}

  onDrop={(e) => {

    e.preventDefault();

    const file =
      e.dataTransfer.files?.[0];

    if (file) {

      handleImageUpload(file);
    }
  }}

  className="
    flex flex-col items-center justify-center
    w-full h-36
    border-2 border-dashed border-purple-500/40
    rounded-xl
    cursor-pointer
    bg-dark-900/40
    hover:bg-dark-800/60
    transition-all
  "
>

  <input
    type="file"
    accept="image/*"
    className="hidden"
    disabled={uploadingImage}

    onChange={(e) => {

      const file =
        e.target.files?.[0];

      if (file) {

        handleImageUpload(file);
      }
    }}
  />

  {uploadingImage ? (

  <div className="flex flex-col items-center gap-3">

    <div className="
      w-10 h-10
      border-4 border-purple-500/20
      border-t-purple-500
      rounded-full
      animate-spin
    " />

    <span className="text-sm text-dark-200">
      Enviando imagem...
    </span>

    <div className="w-40 h-2 bg-dark-700 rounded-full overflow-hidden">

  <div

    className="
      h-full
      bg-purple-500
      transition-all duration-300
    "

    style={{
      width: `${uploadProgress}%`
    }}
  />

</div>

<span className="text-xs text-dark-400">

  {uploadProgress}%

</span>

  </div>

  

) : (

  <div className="flex flex-col items-center gap-2">

    <div className="
      w-12 h-12 rounded-2xl
      bg-purple-600/20
      flex items-center justify-center
      text-purple-400
      text-2xl
    ">
      ↑
    </div>

    <div className="text-sm text-dark-200">
      Clique para enviar imagem
    </div>

    <div className="text-xs text-dark-500">
      JPG, PNG, WEBP • Máx 5MB
    </div>

  </div>

)}
</label>
          )}
          {formData.imageUrl && (
            <div className="relative inline-block max-w-[220px]">
              <img
                src={formData.imageUrl}
                alt="Preview"
                className="
  max-h-[260px]
  w-auto
  rounded-lg
  object-contain
  border border-dark-700
"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Salvando...' : initialData ? 'Atualizar' : 'Adicionar Prompt'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
};

export default PromptForm;
